import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Import, Loader2, Lock, ShieldAlert, AlertCircle, CheckCircle2, Ban } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { PhoneInput } from '@/components/ui/phone-input';
import { createPhoneNumber } from '@/api/phone-numbers/phone_numbers.api';
import { useEnhancedToast } from "@/components/ui/enhanced-toast";
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useAuth } from '@/context/AuthContext';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Twilio import form schema
const twilioFormSchema = z.object({
    phoneNumber: z
        .string()
        .min(1, 'Phone number is required')
        .refine((val) => !val || (val.startsWith('+') && isValidPhoneNumber(val)), {
            message: 'Please enter a valid phone number with country code (e.g., +49...)'
        }),
    label: z
        .string()
        .min(1, 'Label is required')
        .max(50, 'Label must be less than 50 characters'),
});

// SIP import form schema
const sipFormSchema = z.object({
    phoneNumber: z
        .string()
        .min(1, 'Phone number is required')
        .refine((val) => !val || (val.startsWith('+') && isValidPhoneNumber(val)), {
            message: 'Please enter a valid phone number with country code (e.g., +49...)'
        }),
    sipTerminationUri: z
        .string()
        .min(1, 'SIP Termination URI is required')
        .refine((val) => {
            // Allow both SIP URIs and direct hostnames
            return val.startsWith('sip:') ? /^sip:.+@.+\..+$/.test(val) : /^[a-zA-Z0-9.-]+\.[a-zA-Z0-9.-]+/.test(val);
        }, {
            message: 'Please enter a valid SIP URI (e.g., sip:username@domain.com) or hostname (e.g., mycloudonixtrunk.pstn.twilio.com)'
        }),
    label: z
        .string()
        .min(1, 'Label is required')
        .max(50, 'Label must be less than 50 characters'),
});

type TwilioFormValues = z.infer<typeof twilioFormSchema>;
type SipFormValues = z.infer<typeof sipFormSchema>;

interface ImportDialogProps {
    onRefresh: () => void;
    triggerButton?: React.ReactNode;
}

export const ImportPhoneNumberDialog = ({ onRefresh, triggerButton }: ImportDialogProps) => {
    const [open, setOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [activeTab, setActiveTab] = useState<string>("twilio");
    const { error, success } = useEnhancedToast();
    const { user } = useAuth();
    const canImportPhoneNumber = user?.permissions?.phone_numbers === 'EDITOR';

    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            #copyButton .copy-icon {
                display: block;
            }
            #copyButton .check-icon {
                display: none;
            }
            #copyButton.copied .copy-icon {
                display: none;
            }
            #copyButton.copied .check-icon {
                display: block;
                color: #22c55e;
            }
        `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    // Create refs for toast functions
    const errorRef = useRef(error);
    const successRef = useRef(success);

    // Update refs when toast functions change
    useEffect(() => {
        errorRef.current = error;
        successRef.current = success;
    }, [error, success]);

    // Twilio form
    const twilioForm = useForm<TwilioFormValues>({
        resolver: zodResolver(twilioFormSchema),
        defaultValues: {
            phoneNumber: '',
            label: '',
        },
    });

    // SIP form
    const sipForm = useForm<SipFormValues>({
        resolver: zodResolver(sipFormSchema),
        defaultValues: {
            phoneNumber: '',
            sipTerminationUri: '',
            label: '',
        },
    });

    const onSubmitTwilio = async (data: TwilioFormValues) => {
        if (!canImportPhoneNumber) {
            errorRef.current({
                icon: Ban,
                title: "Permission Denied",
                description: "You need editor permissions to import phone numbers",
                duration: 4000,
            });
            return;
        }

        try {
            setIsSubmitting(true);

            const response = await createPhoneNumber({
                provider: 'twilio',
                number: data.phoneNumber,
                name: data.label,
            });

            if (response.error) {
                throw new Error(response.error);
            }

            // First close dialog and reset form
            setOpen(false);
            twilioForm.reset();

            // Then show success message
            successRef.current({
                icon: CheckCircle2,
                title: "Phone Number Imported",
                description: "The Twilio phone number has been successfully imported and is ready to use.",
                duration: 4000,
            });

            // Finally refresh the list
            onRefresh();

        } catch {
            errorRef.current({
                icon: AlertCircle,
                title: "Import Failed",
                description: "Unable to import this phone number. Please contact your support team to provision this number before importing it here.",
                duration: 5000,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const onSubmitSip = async (data: SipFormValues) => {
        if (!canImportPhoneNumber) {
            errorRef.current({
                icon: Ban,
                title: "Permission Denied",
                description: "You need editor permissions to import phone numbers",
                duration: 4000,
            });
            return;
        }

        try {
            setIsSubmitting(true);

            const response = await createPhoneNumber({
                provider: 'byo-phone-number',
                number: data.phoneNumber,
                name: data.label,
                sipTerminationUri: data.sipTerminationUri,
                // numberE164CheckEnabled is set to true in the API
            });

            if (response.error) {
                throw new Error(response.error);
            }

            // First close dialog and reset form
            setOpen(false);
            sipForm.reset();

            // Then show success message
            successRef.current({
                icon: CheckCircle2,
                title: "SIP Phone Number Imported",
                description: "The SIP phone number has been successfully imported and is ready to use.",
                duration: 4000,
            });

            // Finally refresh the list
            onRefresh();

        } catch {
            errorRef.current({
                icon: AlertCircle,
                title: "Import Failed",
                description: "Unable to import this SIP phone number. Please verify your SIP configuration and contact your support team if the issue persists.",
                duration: 5000,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <TooltipProvider delayDuration={200}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span>
                            <DialogTrigger asChild>
                                {triggerButton || (
                                    <Button
                                        size="sm"
                                        className="gap-2"
                                        disabled={!canImportPhoneNumber}
                                    >
                                        <Import className="h-4 w-4" />
                                        Import Number
                                        {!canImportPhoneNumber && <Lock className="w-3.5 h-3.5 ml-2 opacity-70" />}
                                    </Button>
                                )}
                            </DialogTrigger>
                        </span>
                    </TooltipTrigger>
                    {!canImportPhoneNumber && (
                        <TooltipContent side="bottom" sideOffset={5} className="flex items-center gap-2 max-w-xs">
                            <ShieldAlert className="w-12 h-12 text-amber-500" />
                            <p>You need editor permissions to import phone numbers. Contact your administrator for access.</p>
                        </TooltipContent>
                    )}
                </Tooltip>
            </TooltipProvider>
            <DialogContent className="sm:max-w-[650px] w-[95vw] p-0">
                <DialogHeader className="border-b bg-muted/20 px-6 py-3">
                    <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                            <Import className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 space-y-1">
                            <DialogTitle className="text-base font-semibold">
                                Import Phone Number
                            </DialogTitle>
                            <p className="text-xs text-muted-foreground">
                                Import your existing phone numbers from Twilio or configure SIP connectivity.
                            </p>
                        </div>
                    </div>
                </DialogHeader>
                
                <div className="px-6 pt-4 pb-6">
                    <Tabs defaultValue="twilio" value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                        <TabsList className="h-11 w-full">
                            <TabsTrigger value="twilio" className="gap-2 px-4 flex-1">
                                Twilio
                            </TabsTrigger>
                            <TabsTrigger value="sip" className="gap-2 px-4 flex-1">
                                SIP
                            </TabsTrigger>
                        </TabsList>
                        
                        <TabsContent value="twilio" className="space-y-4 min-h-[320px]">
                            <p className="text-xs text-muted-foreground">
                                Import your Twilio phone numbers. Twilio credentials are managed by the system.
                            </p>
                            <Form {...twilioForm}>
                                <form
                                    key="twilio-import-form"
                                    onSubmit={twilioForm.handleSubmit(onSubmitTwilio)}
                                    className="space-y-4"
                                    autoComplete="new-password"
                                >
                                    <FormField
                                        control={twilioForm.control}
                                        name="phoneNumber"
                                        render={({ field }) => (
                                            <FormItem className="space-y-2">
                                                <FormLabel className="text-sm font-medium">Twilio Phone Number</FormLabel>
                                                <FormControl>
                                                    <PhoneInput
                                                        value={field.value}
                                                        onChange={field.onChange}
                                                        defaultCountry="DE"
                                                        international
                                                    />
                                                </FormControl>
                                                <FormMessage className="text-xs" />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={twilioForm.control}
                                        name="label"
                                        render={({ field }) => (
                                            <FormItem className="space-y-2">
                                                <FormLabel className="text-sm font-medium">Label</FormLabel>
                                                <FormControl>
                                                    <Input
                                                        placeholder="e.g., Main Support Line"
                                                        className="h-9"
                                                        {...field}
                                                    />
                                                </FormControl>
                                                <FormMessage className="text-xs" />
                                            </FormItem>
                                        )}
                                    />
                                </form>
                            </Form>
                        </TabsContent>

                        <TabsContent value="sip" className="space-y-4 min-h-[320px]">
                            <p className="text-xs text-muted-foreground">
                                Import your SIP phone numbers. Provide your SIP termination details to connect.
                            </p>
                            <Form {...sipForm}>
                                <form
                                    key="sip-import-form"
                                    onSubmit={sipForm.handleSubmit(onSubmitSip)}
                                    className="space-y-4"
                                    autoComplete="new-password"
                                >
                                    <FormField
                                        control={sipForm.control}
                                        name="phoneNumber"
                                        render={({ field }) => (
                                            <FormItem className="space-y-2">
                                                <FormLabel className="text-sm font-medium">Phone Number</FormLabel>
                                                <FormControl>
                                                    <PhoneInput
                                                        value={field.value}
                                                        onChange={field.onChange}
                                                        defaultCountry="DE"
                                                        international
                                                    />
                                                </FormControl>
                                                <FormMessage className="text-xs" />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={sipForm.control}
                                        name="sipTerminationUri"
                                        render={({ field }) => (
                                            <FormItem className="space-y-2">
                                                <FormLabel className="text-sm font-medium">SIP Termination URI</FormLabel>
                                                <FormControl>
                                                    <Input
                                                        placeholder="sip:username@domain.com or hostname"
                                                        className="h-9"
                                                        {...field}
                                                    />
                                                </FormControl>
                                                <FormMessage className="text-xs" />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={sipForm.control}
                                        name="label"
                                        render={({ field }) => (
                                            <FormItem className="space-y-2">
                                                <FormLabel className="text-sm font-medium">Label</FormLabel>
                                                <FormControl>
                                                    <Input
                                                        placeholder="e.g., SIP Trunk Line"
                                                        className="h-9"
                                                        {...field}
                                                    />
                                                </FormControl>
                                                <FormMessage className="text-xs" />
                                            </FormItem>
                                        )}
                                    />
                                </form>
                            </Form>
                        </TabsContent>
                    </Tabs>
                </div>

                <DialogFooter className="border-t bg-muted/20 px-6 py-3 gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setOpen(false)}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        size="sm"
                        className="gap-2"
                        disabled={isSubmitting || !canImportPhoneNumber}
                        onClick={() => {
                            if (activeTab === 'twilio') {
                                twilioForm.handleSubmit(onSubmitTwilio)();
                            } else {
                                sipForm.handleSubmit(onSubmitSip)();
                            }
                        }}
                    >
                        {isSubmitting ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Importing...
                            </>
                        ) : (
                            <>
                                <Import className="h-4 w-4" />
                                Import Number
                                {!canImportPhoneNumber && <Lock className="w-3.5 h-3.5 ml-1 opacity-70" />}
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
